import { Injectable, inject, DOCUMENT } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { of } from 'rxjs';
import { GbrUser } from '@gbr-monorepo/interfaces';
import { LanguageCode } from '@gbr-monorepo/interfaces';
import { SetUser, SetUserLanguage } from './user.action';
import { UserService } from '../../shared/services/user.service';

import { TranslateService } from '@ngx-translate/core';

export interface UserStateModel {
    user?: GbrUser;
    isAuthenticated: boolean;
    allowProfilePicture?: boolean;
    profilePicture?: string;
    language: LanguageCode | undefined;
}

@State<UserStateModel>({
    name: 'userState',
    defaults: {
        user: undefined,
        isAuthenticated: false,
        allowProfilePicture: false,
        profilePicture: undefined,
        language: undefined
    }
})
@Injectable()
export class UserState {
    private userService = inject(UserService);
    private document = inject<Document>(DOCUMENT);
    private translate = inject(TranslateService);

    @Selector()
    static user(state: UserStateModel): GbrUser | undefined {
        return state.user;
    }

    @Selector()
    static allowProfilePicture(state: UserStateModel): boolean | undefined {
        return state.allowProfilePicture;
    }

    @Selector()
    static profilePicture(state: UserStateModel): string | undefined {
        return state.profilePicture;
    }

    @Selector()
    static userLanguage(state: UserStateModel): LanguageCode {
        return state.language ? state.language : LanguageCode.EN;
    }

    @Action(SetUser)
    public setUser({ patchState }: StateContext<UserStateModel>, { payload }: SetUser) {
        return of(patchState({ user: payload.user, isAuthenticated: payload.isAuthenticated }));
    }

    @Action(SetUserLanguage)
    public setUserLanguage({ getState, patchState }: StateContext<UserStateModel>, { payload }: SetUserLanguage) {
        const { language: currentLanguage } = getState();
        const { language } = payload;
        this.translate.use(language);
        this.document.documentElement.lang = language;
        patchState({ language });
        if (currentLanguage && currentLanguage !== language) {
            return this.userService.patchMe(language);
        }
        return of(null);
    }
}
